import {
  Card,
  CardContent,
  CardHeader,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormikHelpers, useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, useCallback, useState } from "react";
import * as Yup from "yup";

import {
  CreatePersonalizedGifMutation,
  CreatePersonalizedGifMutationVariables,
} from "~frontend/__generated__/graphql";
import { FormikDialog } from "~frontend/components/forms";
import {
  CREATE_PERSONALIZED_GIF,
  LIST_PERSONALIZED_GIFS,
} from "~frontend/graphql/personalizedGifs";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CreateOrEditGifFormActions } from "./CreateOrEditGifFormActions";
import { CustomizeDesignFields } from "./CustomizeDesignFields";
import { CustomizeTextFields } from "./CustomizeTextFields";
import { FormGifPreview } from "./FormGifPreview";
import { SelectTemplateField } from "./SelectTemplateField";
import { CreateOrEditGifFormValues, CreatomateTemplate } from "./types";

enum FormStage {
  SELECT_TEMPLATE,
  CUSTOMIZE_TEXT,
  CUSTOMIZE_DESIGN,
  PREVIEW,
}

const formStageLabels: { [key in FormStage]: string } = {
  [FormStage.SELECT_TEMPLATE]: "Pick a Template",
  [FormStage.CUSTOMIZE_TEXT]: "Customize Text",
  [FormStage.CUSTOMIZE_DESIGN]: "Customize Design",
  [FormStage.PREVIEW]: "Preview",
};

export function CreateGifDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [formStage, setFormStage] = useState<FormStage>(
    FormStage.SELECT_TEMPLATE
  );

  const [createPersonalizedGif] = useMutation<
    CreatePersonalizedGifMutation,
    CreatePersonalizedGifMutationVariables
  >(CREATE_PERSONALIZED_GIF, {
    onErrorSnackbarMessage: "Error saving GIF",
    refetchQueries: [LIST_PERSONALIZED_GIFS],
    awaitRefetchQueries: true,
  });

  const handleSubmit = useCallback(
    async (
      values: CreateOrEditGifFormValues,
      formik: FormikHelpers<CreateOrEditGifFormValues>
    ) => {
      if (!values.name || !values.template) {
        return;
      }

      const { errors } = await createPersonalizedGif({
        variables: {
          input: {
            subaccountId: subaccount.id,
            name: values.name,
            creatomateTemplateId: values.template.id,
            customFields: values.template.requiredCustomFields.map(field => ({
              ..._.pick(field, "name", "friendlyName"),
              value: values.customFields[field.name],
            })),
            customColors: values.template.colorVariables.map(colorVariable => ({
              ..._.pick(
                colorVariable,
                "name",
                "friendlyName",
                "modificationKeys"
              ),
              value: values.customColors[colorVariable.name],
            })),
            customLogo: values.template.logoUrlKey
              ? { key: values.template.logoUrlKey, url: values.logoUrl ?? null }
              : null,
            previewSnapshotTimeSeconds:
              values.template.previewSnapshotTimeSeconds,
            contactFieldMetadata: values.template.requiredContactFields.map(
              field => ({
                ..._.pick(field, "name"),
                fallback: values.customFields[field.name] || "",
              })
            ),
          },
        },
      });

      if (!errors) {
        onClose();
        formik.resetForm();
        setFormStage(FormStage.SELECT_TEMPLATE);
      }
    },
    [createPersonalizedGif, subaccount, onClose, setFormStage]
  );

  const handleClose = useCallback(() => {
    onClose();
    setFormStage(FormStage.SELECT_TEMPLATE);
  }, [setFormStage, onClose]);

  return (
    <FormikDialog<CreateOrEditGifFormValues>
      open={open}
      initialValues={{
        template: null,
        name: "",
        customFields: {},
        customColors: {},
        logoUrl: null,
      }}
      validationSchema={Yup.object().shape({
        template: Yup.object().required(),
        name: Yup.string().required("Name is required"),
        customFields: Yup.object().when(
          "template",
          (template: CreatomateTemplate | null, schema) => {
            if (!template) {
              return schema;
            }
            const shape: Record<string, Yup.AnySchema> = {};
            // Build up the validation schema for the required fields
            for (const customField of template.requiredCustomFields) {
              shape[customField.name] = Yup.string()
                .required(`${customField.friendlyName} is required`)
                .max(
                  customField.charLimit,
                  ({ max }) =>
                    `${customField.friendlyName} must be at most ${max} characters`
                );
            }

            // Build up the validation schema for the contact fallback fields
            for (const contactField of template.requiredContactFields) {
              shape[contactField.name] = Yup.string().max(
                contactField.charLimit,
                ({ max }) => `Cannot be longer than ${max} characters`
              );
            }
            return schema.shape(shape);
          }
        ),
        customColors: Yup.object(),
        logoUrl: Yup.string().nullable(),
      })}
      validateOnMount
      onSubmit={handleSubmit}
    >
      <CreateGifDialogInner
        onClose={handleClose}
        formStage={formStage}
        onChangeFormStage={setFormStage}
      />
    </FormikDialog>
  );
}

function CreateGifDialogInner({
  onClose,
  formStage,
  onChangeFormStage,
}: {
  onClose: () => void;
  formStage: FormStage;
  onChangeFormStage: (newStage: FormStage) => void;
}) {
  const form = useFormikContext<CreateOrEditGifFormValues>();

  const handleClose = () => {
    onClose();
    form.resetForm();
  };

  const renderActions = () => {
    switch (formStage) {
      case FormStage.SELECT_TEMPLATE:
        return (
          <CreateOrEditGifFormActions
            onClickNext={() => onChangeFormStage(FormStage.CUSTOMIZE_TEXT)}
            onClickCancel={handleClose}
            nextDisabled={
              !!form.errors.template || form.values.template === null
            }
          />
        );
      case FormStage.CUSTOMIZE_TEXT:
        return (
          <CreateOrEditGifFormActions
            onClickBack={() => onChangeFormStage(FormStage.SELECT_TEMPLATE)}
            onClickNext={() => onChangeFormStage(FormStage.CUSTOMIZE_DESIGN)}
            onClickCancel={handleClose}
            nextDisabled={!!form.errors.name || !!form.errors.customFields}
          />
        );
      case FormStage.CUSTOMIZE_DESIGN:
        return (
          <CreateOrEditGifFormActions
            onClickBack={() => onChangeFormStage(FormStage.CUSTOMIZE_TEXT)}
            onClickNext={() => onChangeFormStage(FormStage.PREVIEW)}
            onClickCancel={handleClose}
            nextDisabled={!!form.errors.customColors}
          />
        );
      case FormStage.PREVIEW:
        return (
          <CreateOrEditGifFormActions
            onClickBack={() => onChangeFormStage(FormStage.CUSTOMIZE_DESIGN)}
            onClickCancel={handleClose}
            onClickSubmit={form.submitForm}
          />
        );
    }
  };

  const renderForm = () => {
    switch (formStage) {
      case FormStage.SELECT_TEMPLATE:
        return <SelectTemplateField />;
      case FormStage.CUSTOMIZE_TEXT:
        return <CustomizeTextFields />;
      case FormStage.CUSTOMIZE_DESIGN:
        return <CustomizeDesignFields />;
      case FormStage.PREVIEW:
        return <FormGifPreview />;
    }
  };

  return (
    <>
      <DialogTitle>New Personalized GIF</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ paddingBottom: "24px" }}>
          Use one of the templates below to create a personalized gif that maps
          to the contact fields in your campaign.
        </Typography>
        <Card sx={{ border: "none" }}>
          <CardHeader title={formStageLabels[formStage]} />
          <CardContent
            sx={{
              backgroundColor: palette.lightestGray,
              maxHeight: "500px",
              overflow: "scroll",
            }}
          >
            {renderForm()}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </>
  );
}
